'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Molding type
//***********************************************************************************
//***********************************************************************************

import { fh_add, fh_mul, fh_polygon, fh_solid } from '@acenv/fh-3d-viewer';
import { cn_element_type } from './cn_element_type';
import { cn_configuration, cn_configuration_choice, cn_configuration_line, cn_configuration_param, cn_configuration_param_group, cn_configuration_tab } from './cn_configuration';
import { code_to_label, code_to_rgbcolor } from './cn_opening_type';
import { cn_element_type_visitor } from '..';

export const MOLDING_MATERIAL_LIST = [
    { label: 'Bois', code: 'wood', color: [150, 125, 100, 1] },
    { label: 'PVC', code: 'pvc', color: [200, 200, 200, 1] },
    { label: 'Platre', code: 'gypsum', color: [240, 240, 240, 1] },
    { label: 'Carrelage', code: 'tile', color: [200, 220, 240, 1] },
    { label: 'Ciment', code: 'concrete', color: [150, 150, 150, 1] },
    { label: 'Fibrociment', code: 'fibercement', color: [150, 150, 180, 1] }
];

/**
 * @class cn_molding_type
 * Common class type for moldings
 */
export class cn_molding_type extends cn_element_type {
    //***********************************************************************************
    /**
     * Constructor
     */
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_molding_type';
        this.name = '';
        this.height = 0.08;
        this.thickness = 0.01;
        this.z = 0;

        this.material = 'wood';
    }

    //***********************************************************************************
    /**
     * Creates a default beam type
     * @returns {cn_molding_type[]}
     */
    static default_types(parent) {
        const def = [];

        const base = new cn_molding_type(parent);
        def.push(base);

        const mold1 = new cn_molding_type(parent);
        mold1.z = 1;
        mold1.material = 'wood';
        def.push(mold1);

        const mold = new cn_molding_type(parent);
        mold.z = 2;
        mold.material = 'gypsum';
        def.push(mold);

        return def;
    }

    //***********************************************************************************
    get_generic_label() {
        return 'Type de poutre et poteau';
    }

    //***********************************************************************************
    /**
     * Returns a clone of this
     * @returns {cn_molding_type}
     */
    clone() {
        const c = new cn_molding_type(this.parent);
        c.name = this.name;
        c.height = this.height;
        c.thickness = this.thickness;
        c.z = this.z;
        c.material = this.material;

        return c;
    }

    //***********************************************************************************
    /**
     * Returns the list of keys of the element
     * @returns {string[]}
     */
    model_keys() {
        return ['name', 'height', 'thickness', 'material', 'z'];
    }

    //***********************************************************************************
    /**
     * Serialize
     * @returns {object}
     */
    serialize() {
        const json = this.serialize_element();
        json.class_name = 'cn_molding_type';
        json.name = this.name;
        json.height = this.height;
        json.thickness = this.thickness;
        json.z = this.z;
        json.material = this.material;

        return json;
    }

    //***********************************************************************************
    /**
     * Unserialize
     * @param {object} json
     * @returns {cn_molding_type}
     */
    static unserialize(json, parent) {
        const et = new cn_molding_type(parent);
        et.unserialize_element(json);
        et.name = json.name;
        et.height = json.height;
        et.thickness = json.thickness;
        et.z = json.z;

        if (typeof (json.material) == 'string' && code_to_label(json.material, MOLDING_MATERIAL_LIST)) et.material = json.material;
        return et;
    }

    //***********************************************************************************
    /**
     * Returns a displayble label
     * @returns {string}
     */
    get_label() {
        if (this.is_null) return "Aucun";
        if (this.name !== '') return this.name;
        let html = "Plinthe ";
        if (this.z > 0)
            html = `Moulure ${this.z.toFixed(2)}m `
        html += code_to_label(this.material, MOLDING_MATERIAL_LIST);
        html += ` ${Math.round(this.height * 1000)}mm`;
        return html;
    }

    //***********************************************************************************
    /**
     * Returns an array of objects describing the type.
     * @returns {{label: string, value?: any, decimals?: number, unit?: string}[]}
     */
    get_description() {
        const description = [];

        const material_label = code_to_label(this.material, MOLDING_MATERIAL_LIST);
        if (material_label)
            description.push({ label: 'Matériau', value: material_label });

        description.push({ label: 'Hauteur', value: this.z, decimals: 2, unit: 'm' });

        description.push({ label: 'Largeur', value: this.height * 1000, decimals: 0, unit: 'mm' });

        description.push({ label: 'Epaisseur', value: this.thickness * 1000, decimals: 0, unit: 'mm' });

        return description;
    }

    //***********************************************************************************
    /**
     * Returns a html string to be put in a <svg>
     * @param {number} w : width of svg element
     * @param {number} h : height of svg element
     * @returns {string}
     */
    draw_svg_icon(w, h) {
        if (this.is_null) return super.draw_svg_icon(w, h);
        let html = ``;

        let sh = this.height * h / 0.3;
        if (sh > h / 2) sh = h / 2;
        let y0 = (this.z > 0) ? (h - sh) / 2 : h - sh;

        let color = code_to_rgbcolor(this.material, MOLDING_MATERIAL_LIST);
        html += `<rect x="0" y="0" width="${w}" height="${h}" fill="rgb(230,230,230)" />`;
        html += `<rect class="molding_type" x="0" y="${y0}" width="${w}" height="${sh}" stroke="black" fill="${color}" />`;

        return html;
    }

    //***********************************************************************************
    //**** Get configuration
    //***********************************************************************************
    static configuration() {
        const configuration = new cn_configuration();

        //*** Global params
        const param_group = new cn_configuration_param_group('Dimensions', 'dimensions');
        configuration.add_param_group(param_group);
        param_group.add_param(new cn_configuration_param('Décalage', 'z', 0, 1000, 'cm'));
        param_group.add_param(new cn_configuration_param('Hauteur', 'height', 10, 10000, 'mm'));
        param_group.add_param(new cn_configuration_param('Epaisseur', 'thickness', 10, 10000, 'mm'));

        const tab = new cn_configuration_tab('Typologie', 'standard');
        configuration.add_tab(tab);

        //***********************************************
        //*** Material
        const material_list = new cn_configuration_line('Matériau', 'material');
        tab.add_line(material_list);
        MOLDING_MATERIAL_LIST.forEach((material) => {
            material_list.add_choice(new cn_configuration_choice(material.label, material.code));
        });

        return configuration;
    }


    //***********************************************************************************
    //**** Fill configuration
    //***********************************************************************************
    /**
     * Returns a new configuration built from the element type, or fills an existing configuration.
     * @param {cn_configuration} config
     * @returns {cn_configuration}
     */
    fill_configuration(config = null) {
        const configuration = (config) ? config : cn_molding_type.configuration();
        // tab selected
        const selection = configuration.selection;
        const param_group = configuration.get_param_group('dimensions');
        param_group.set_param('z', this.z * 100);
        param_group.set_param('height', this.height * 1000);
        param_group.set_param('thickness', this.thickness * 1000);

        const material_line = configuration.tabs[selection].get_line('material');
        material_line.set_selection(this.material);

        return configuration;
    }

    //***********************************************************************************
    //**** Load configuration
    //***********************************************************************************
    load_configuration(configuration, actual_change = false) {
        const param_group = configuration.get_param_group('dimensions');
        this.z = param_group.get_param('z').value / 100;
        this.height = param_group.get_param('height').value / 1000;
        this.thickness = param_group.get_param('thickness').value / 1000;

        // tab selected
        const selection = configuration.selection;
        const configuration_tab = configuration.tabs[selection];
        this.material = configuration_tab.get_line('material').get_selection();
        return true;
    }

    /**
     * Accept element type visitor
     *
     * @param {cn_element_type_visitor} element_type_visitor
     */
    accept_visitor(element_type_visitor) {
        element_type_visitor.visit_molding_type(this);
    }
}

